import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Video, X, Info, CheckCircle2, XCircle, Grid2x2, Grid3x3, Square, LayoutGrid, Plus,
  ChevronDown, Cctv, FolderTree, Network, Eraser,
} from 'lucide-react';
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors,
  DragOverlay, type DragEndEvent, type DragStartEvent,
} from '@dnd-kit/core';
import { devicesApi } from '@/services/api';
import { videoAssuranceApi, locationsApi, type TreeNode } from '@/lib/inventory-api';
import { UnifiedCameraPlayer, type CameraFeed } from '@/components/video/UnifiedCameraPlayer';
import { StatusPill } from '@/components/ui/primitives';
import { NODE_ICON, scoreColor } from '@/components/LogicalTree';

// Video Wall — a configurable NxN grid of live camera tiles (Commander
// "video-wall" style, in IOSentra's glass design). A camera picker fills tiles;
// each tile is a UnifiedCameraPlayer (offline / no-signal cameras show the
// player's built-in placeholder). An info button opens a per-camera detail
// drawer with the Video Assurance panel. No tabs.
interface Cam { id: string; name: string; driverInstanceId?: string; channel?: number; status?: string }

const LAYOUTS = [
  { n: 1, label: '1×1', icon: Square },
  { n: 2, label: '2×2', icon: Grid2x2 },
  { n: 3, label: '3×3', icon: Grid3x3 },
  { n: 4, label: '4×4', icon: LayoutGrid },
] as const;

const vaColor = (s: number) => (s >= 90 ? 'text-ok' : s >= 75 ? 'text-warning' : 'text-critical');

const toFeed = (c: Cam): CameraFeed => ({
  id: c.id, deviceId: c.id, instanceId: c.driverInstanceId ?? '', channel: c.channel ?? 1,
  name: c.name, status: c.status === 'online' ? 'online' : 'offline', recording: false,
});

export function CamerasPage() {
  const { t } = useTranslation('pages');
  const { data } = useQuery({ queryKey: ['devices', 'cameras'], queryFn: async () => devicesApi.list({ category: 'camera' }) });
  const cameras = useMemo<Cam[]>(() => (Array.isArray(data) ? (data as unknown as Cam[]) : []), [data]);

  const [grid, setGrid] = useState(2);                       // NxN layout
  const [tiles, setTiles] = useState<(string | null)[]>([]); // camera id per cell (null = empty)
  const [pickerOpen, setPickerOpen] = useState(true);
  const [detailId, setDetailId] = useState<string | null>(null);

  // The wall starts EMPTY and never auto-loads cameras — the operator places
  // them deliberately (click in the picker, or drag onto a tile). This keeps a
  // freshly-opened Live Video page from firing up N streams unasked. The effect
  // below only resizes the tile array to match the grid; it never fills tiles.
  const cells = grid * grid;
  useEffect(() => {
    setTiles((prev) => {
      const next: (string | null)[] = prev.slice(0, cells);
      while (next.length < cells) next.push(null);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cells]);

  // Empty the whole wall.
  const clearWall = () => setTiles(Array(cells).fill(null));
  const hasAny = tiles.some((t) => t !== null);

  const camById = (id: string | null) => (id ? cameras.find((c) => c.id === id) ?? null : null);
  const assignToFirstFree = (id: string) =>
    setTiles((prev) => {
      if (prev.includes(id)) return prev.map((t) => (t === id ? null : t)); // toggle off if already placed
      const i = prev.findIndex((t) => t === null);
      if (i === -1) return prev;
      const next = [...prev]; next[i] = id; return next;
    });
  const clearTile = (idx: number) => setTiles((prev) => prev.map((t, i) => (i === idx ? null : t)));
  // Drop a camera into a specific tile (drag-and-drop). Moves it if already
  // placed elsewhere (clears the old cell) so a camera never appears twice.
  const placeInTile = (idx: number, id: string) => {
    setTiles((prev) => prev.map((t, i) => (i === idx ? id : t === id ? null : t)));
  };

  // ── Drag-and-drop: drag a camera from the tree onto a tile to place it ──
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const [dragName, setDragName] = useState<string | null>(null);
  const onDragStart = (e: DragStartEvent) => setDragName((e.active.data.current?.name as string) ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setDragName(null);
    const id = e.active.data.current?.deviceId as string | undefined;
    const tileIdx = e.over?.data.current?.tileIdx as number | undefined;
    if (!id || tileIdx === undefined) return;
    placeInTile(tileIdx, id);
  };

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">{t('cameras.title')}</h1>
        <div className="flex items-center gap-2">
          {/* Layout selector */}
          <div className="flex rounded-md bg-white/5 p-0.5 ring-1 ring-white/10">
            {LAYOUTS.map(({ n, label, icon: Icon }) => (
              <button
                key={n}
                onClick={() => setGrid(n)}
                title={label}
                className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs transition-colors ${grid === n ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}
              >
                <Icon size={14} /> {label}
              </button>
            ))}
          </div>
          <button onClick={() => setPickerOpen((o) => !o)} className="tk-btn-ghost px-2" title={t('cameras.actions.cameras')}>
            <Video size={16} /> {t('cameras.actions.cameras')}
          </button>
          {/* Clear the whole wall (stays empty until you place a camera / change layout). */}
          <button
            onClick={clearWall}
            disabled={!hasAny}
            className="tk-btn-ghost px-2 disabled:opacity-40"
            title={t('cameras.actions.clear')}
          >
            <Eraser size={16} /> {t('cameras.actions.clear')}
          </button>
        </div>
      </div>

      <div className={`grid gap-4 ${pickerOpen ? 'lg:grid-cols-[240px_1fr]' : 'grid-cols-1'}`}>
        {/* Camera picker — Physical / Logical asset tree */}
        {pickerOpen && (
          <CameraTreePicker
            cameraIds={new Set(cameras.map((c) => c.id))}
            placed={new Set(tiles.filter((t): t is string => t !== null))}
            onToggle={assignToFirstFree}
            count={cameras.length}
          />
        )}

        {/* Tile grid — each cell is a drop target for a dragged camera. */}
        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${grid}, minmax(0, 1fr))` }}>
          {tiles.map((id, idx) => {
            const cam = camById(id);
            return (
              <TileDropZone key={idx} idx={idx}>
                {(isOver) => (cam ? (
                  <div className={`group relative aspect-video overflow-hidden rounded-lg bg-black ring-1 transition-shadow ${isOver ? 'ring-2 ring-primary' : 'ring-white/10'}`}>
                    <UnifiedCameraPlayer camera={toFeed(cam)} onRetry={() => undefined} />
                    {/* Overlay: name + status (top-left), actions (top-right, on hover) */}
                    <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between bg-gradient-to-b from-black/70 to-transparent px-2 py-1.5">
                      <div className="flex items-center gap-1.5 text-xs font-medium text-white">
                        <span className={`h-1.5 w-1.5 rounded-full ${cam.status === 'online' ? 'bg-ok' : 'bg-critical'}`} />
                        <span className="truncate">{cam.name}</span>
                      </div>
                      <div className="pointer-events-auto flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                        <button onClick={() => setDetailId(cam.id)} className="grid h-6 w-6 place-items-center rounded bg-black/40 text-white hover:bg-black/70" title={t('cameras.actions.cameraDetails')}><Info size={13} /></button>
                        <button onClick={() => clearTile(idx)} className="grid h-6 w-6 place-items-center rounded bg-black/40 text-white hover:bg-black/70" title={t('cameras.actions.remove')}><X size={13} /></button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setPickerOpen(true)}
                    className={`grid aspect-video w-full place-items-center rounded-lg border border-dashed bg-white/[0.02] text-muted transition-colors ${isOver ? 'border-primary bg-primary/10 text-primary' : 'border-white/10 hover:bg-white/5'}`}
                  >
                    <span className="flex flex-col items-center gap-1 text-xs"><Plus size={18} /> {isOver ? t('cameras.tile.dropToAdd') : t('cameras.tile.addCamera')}</span>
                  </button>
                ))}
              </TileDropZone>
            );
          })}
        </div>
      </div>

      {/* Detail drawer */}
      {detailId && <CameraDetailDrawer cameraId={detailId} cam={camById(detailId)} onClose={() => setDetailId(null)} />}
    </div>
      {/* Drag preview — the camera name following the cursor. */}
      <DragOverlay>
        {dragName ? (
          <div className="flex items-center gap-1.5 rounded-md bg-primary/90 px-2.5 py-1.5 text-xs font-medium text-white shadow-lg">
            <Cctv size={13} /> {dragName}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

// Slide-over drawer: live/playback player + per-camera Video Assurance panel.
function CameraDetailDrawer({ cameraId, cam, onClose }: { cameraId: string; cam: Cam | null; onClose: () => void }) {
  const { t } = useTranslation('pages');
  const [mode, setMode] = useState<'live' | 'playback'>('live');
  // Default the playback window to "the last hour" (datetime-local string).
  const [pbStart, setPbStart] = useState(() => {
    const t = new Date(Date.now() - 60 * 60 * 1000);
    t.setSeconds(0, 0);
    const off = t.getTimezoneOffset();
    return new Date(t.getTime() - off * 60000).toISOString().slice(0, 16);
  });
  const startDate = new Date(pbStart);
  const endDate = new Date(startDate.getTime() + 60 * 60 * 1000); // 1h window
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 flex h-full w-full max-w-lg flex-col overflow-y-auto border-l border-white/10 bg-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="truncate text-sm font-semibold">{cam?.name ?? t('cameras.detail.cameraFallback')}</div>
          <button onClick={onClose} className="tk-btn-ghost px-2" aria-label={t('cameras.actions.close')}><X size={16} /></button>
        </div>
        <div className="space-y-4 p-5">
          {cam && (
            <>
              {/* Live / Playback mode toggle + (playback) start-time picker. */}
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-md bg-white/5 p-0.5 text-xs ring-1 ring-white/10">
                  {([['live', t('cameras.detail.live')], ['playback', t('cameras.detail.playback')]] as const).map(([k, lbl]) => (
                    <button key={k} onClick={() => setMode(k)}
                      className={`rounded px-2.5 py-1 transition-colors ${mode === k ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {mode === 'playback' && (
                  <input
                    type="datetime-local" value={pbStart} max={new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                    onChange={(e) => setPbStart(e.target.value)}
                    className="tk-input w-auto py-1 text-xs"
                  />
                )}
              </div>
              <div className="aspect-video overflow-hidden rounded-lg bg-black ring-1 ring-white/10">
                {/* key forces a fresh player when switching mode or seek-time so
                    it re-initialises the live/playback session cleanly. */}
                <UnifiedCameraPlayer
                  key={`${mode}-${mode === 'playback' ? pbStart : 'live'}`}
                  camera={toFeed(cam)}
                  onRetry={() => undefined}
                  initialMode={mode}
                  onModeChange={setMode}
                  playbackStartTime={mode === 'playback' ? startDate : undefined}
                  playbackEndTime={mode === 'playback' ? endDate : undefined}
                />
              </div>
            </>
          )}
          <VideoAssurancePanel cameraId={cameraId} />
        </div>
      </div>
    </div>
  );
}

// §7.8 — per-camera Video Assurance panel + image-quality analysis.
function VideoAssurancePanel({ cameraId }: { cameraId: string }) {
  const { t } = useTranslation('pages');
  const { data, isLoading, isError } = useQuery({ queryKey: ['va-camera', cameraId], queryFn: () => videoAssuranceApi.camera(cameraId) });

  if (isLoading) return <div className="tk-card h-48 animate-pulse" />;
  if (isError || !data) return <div className="tk-card text-sm text-muted">{t('cameras.va.noData')}</div>;

  const panel: Array<[string, boolean]> = [
    [t('cameras.va.panel.heartbeat'), data.panel.heartbeat], [t('cameras.va.panel.recording'), data.panel.recording], [t('cameras.va.panel.retention'), data.panel.retention],
    [t('cameras.va.panel.timeSync'), data.panel.timeSync], [t('cameras.va.panel.imageQuality'), data.panel.imageQuality],
  ];
  const iq: Array<[string, number]> = [
    [t('cameras.va.iq.focusQuality'), data.imageQuality.focus], [t('cameras.va.iq.blurScore'), data.imageQuality.blur], [t('cameras.va.iq.exposure'), data.imageQuality.exposure],
    [t('cameras.va.iq.obstruction'), data.imageQuality.obstruction], [t('cameras.va.iq.sceneStability'), data.imageQuality.sceneStability],
  ];

  return (
    <div className="space-y-4">
      <div className="tk-card space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">{t('cameras.va.heading')}</div>
          <StatusPill status={data.status} />
        </div>
        <div className="flex items-baseline gap-2">
          <span className={`tk-num text-4xl font-semibold ${vaColor(data.vaScore)}`}>{data.vaScore}</span>
          <span className="text-sm text-muted">{t('cameras.va.outOf100')}</span>
        </div>
        <ul className="grid grid-cols-2 gap-x-6 gap-y-2">
          {panel.map(([label, ok]) => (
            <li key={label} className="flex items-center justify-between text-sm">
              <span className="text-muted">{label}</span>
              {ok ? <CheckCircle2 size={16} className="text-ok" /> : <XCircle size={16} className="text-critical" />}
            </li>
          ))}
        </ul>
      </div>

      <div className="tk-card space-y-3">
        <div className="text-sm font-medium">{t('cameras.va.imageQualityAnalysis')}</div>
        {iq.map(([label, val]) => (
          <div key={label}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted">{label}</span>
              <span className={`tk-num font-semibold ${vaColor(val)}`}>{val}%</span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/5">
              <div className="h-full rounded-full" style={{ width: `${Math.max(0, Math.min(100, val))}%`, background: val >= 90 ? '#22c55e' : val >= 75 ? '#eab308' : '#ef4444' }} />
            </div>
          </div>
        ))}
        {data.probableCause && data.probableCause !== 'Nominal' && (
          <div className="border-t border-white/10 pt-2 text-xs text-muted">{t('cameras.va.probableCause')} <span className="text-warning">{data.probableCause}</span></div>
        )}
      </div>
    </div>
  );
}

// ── Camera picker as a Physical / Logical asset tree ──────────────────────
// Physical = driver→NVR→channels (the hardware tree); Logical = the sites
// hierarchy (org→site→building→floor→zone→placed cameras). Click a camera leaf
// to add/remove it from the wall. Reuses /api/v1/locations/tree (same data as
// the Sites & Asset workspace). Non-camera leaves (NVRs/servers) are shown for
// context but aren't selectable.
const isCameraLeaf = (n: TreeNode) =>
  n.type === 'device' && (n.coreType ? n.coreType === 'camera' : (n.children?.length ?? 0) === 0 && n.coreType !== 'nvr');

function CameraTreePicker({
  cameraIds, placed, onToggle, count,
}: { cameraIds: Set<string>; placed: Set<string>; onToggle: (id: string) => void; count: number }) {
  const { t } = useTranslation('pages');
  const { data, isLoading } = useQuery({ queryKey: ['locations', 'tree'], queryFn: locationsApi.tree, retry: false });
  const [view, setView] = useState<'physical' | 'logical'>('physical');
  // Default-expand everything down to leaves the first time the tree loads.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const roots = view === 'physical' ? data?.physical ?? [] : data?.logical ?? [];

  useEffect(() => {
    if (!data) return;
    const ids = new Set<string>();
    const walk = (nodes: TreeNode[]) => nodes.forEach((n) => {
      if (n.children?.length) { ids.add(`${view}:${n.id}`); walk(n.children); }
    });
    walk(roots);
    setExpanded(ids);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, view]);

  const toggleNode = (key: string) =>
    setExpanded((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });

  return (
    <div className="tk-card flex max-h-[78vh] flex-col overflow-hidden p-2">
      <div className="px-2 pb-2 text-xs font-medium uppercase tracking-wider text-muted/70">{t('cameras.picker.title', { count })}</div>
      {/* Physical / Logical toggle */}
      <div className="mx-1 mb-2 flex rounded-md bg-white/5 p-0.5 ring-1 ring-white/10">
        {([['physical', t('cameras.picker.physical'), Network], ['logical', t('cameras.picker.logical'), FolderTree]] as const).map(([v, label, Icon]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-xs transition-colors ${view === v ? 'bg-white/10 text-fg' : 'text-muted hover:text-fg'}`}
          >
            <Icon size={13} /> {label}
          </button>
        ))}
      </div>
      <div className="overflow-y-auto overflow-x-hidden">
        {isLoading && <div className="p-3 text-sm text-muted">{t('cameras.picker.loading')}</div>}
        {!isLoading && roots.length === 0 && <div className="p-3 text-sm text-muted">{t('cameras.picker.empty')}</div>}
        {roots.map((n) => (
          <TreeRow
            key={n.id} node={n} view={view} depth={0}
            expanded={expanded} toggleNode={toggleNode}
            cameraIds={cameraIds} placed={placed} onToggle={onToggle}
          />
        ))}
      </div>
    </div>
  );
}

function TreeRow({
  node, view, depth, expanded, toggleNode, cameraIds, placed, onToggle,
}: {
  node: TreeNode; view: 'physical' | 'logical'; depth: number;
  expanded: Set<string>; toggleNode: (k: string) => void;
  cameraIds: Set<string>; placed: Set<string>; onToggle: (id: string) => void;
}) {
  const { t } = useTranslation('pages');
  const key = `${view}:${node.id}`;
  const hasChildren = (node.children?.length ?? 0) > 0;
  const open = expanded.has(key);
  const camera = isCameraLeaf(node);
  // Only cameras the wall actually knows about (in the current tenant/list) are addable.
  const addable = camera && cameraIds.has(node.id);
  const isPlaced = placed.has(node.id);
  const Icon = camera ? Cctv : (NODE_ICON[node.type] ?? FolderTree);
  const pad = 4 + depth * 18; // match /sites indent

  // Addable cameras are draggable onto a wall tile (click still toggles —
  // the 6px activation distance separates click from drag).
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `cam-${node.id}`, data: { deviceId: node.id, name: node.name }, disabled: !addable,
  });

  return (
    <div>
      <div
        className={`flex min-w-0 items-center gap-1 rounded-md pr-2 text-sm transition-colors ${
          isPlaced ? 'bg-primary/10 text-primary' : addable ? 'text-fg hover:bg-white/5' : 'text-muted'
        } ${isDragging ? 'opacity-40' : ''}`}
        style={{ paddingLeft: pad }}
      >
        {/* chevron / spacer — same idiom as /sites (ChevronDown, rotate when closed) */}
        {hasChildren ? (
          <button onClick={() => toggleNode(key)} className="grid h-5 w-5 place-items-center rounded text-muted hover:text-fg" aria-label={open ? t('cameras.tree.collapse') : t('cameras.tree.expand')}>
            <ChevronDown size={14} className={`transition-transform ${open ? '' : '-rotate-90'}`} />
          </button>
        ) : <span className="w-5" />}
        <Icon size={15} className={`shrink-0 ${camera ? scoreColor(node.score) : 'text-muted'}`} />
        {/* label — drag onto a tile to place; click toggles into the wall */}
        <button
          ref={addable ? setNodeRef : undefined}
          {...(addable ? attributes : {})}
          {...(addable ? listeners : {})}
          onClick={() => (addable ? onToggle(node.id) : hasChildren ? toggleNode(key) : undefined)}
          disabled={!addable && !hasChildren}
          className={`flex min-w-0 flex-1 items-center gap-2 py-1.5 text-left disabled:cursor-default ${addable ? 'cursor-grab active:cursor-grabbing' : ''}`}
          title={addable ? (isPlaced ? t('cameras.tree.clickToRemove') : t('cameras.tree.clickToAdd')) : undefined}
        >
          <span className="truncate">{node.name}</span>
          {!camera && <span className="text-[11px] text-muted">({node.deviceCount})</span>}
        </button>
        {/* trailing health — score% only (status badge removed per request). */}
        {camera && (
          <span className={`tk-num text-xs font-medium ${scoreColor(node.score)}`}>{node.score}%</span>
        )}
      </div>
      {hasChildren && open && (
        <div>
          {node.children.map((c) => (
            <TreeRow
              key={c.id} node={c} view={view} depth={depth + 1}
              expanded={expanded} toggleNode={toggleNode}
              cameraIds={cameraIds} placed={placed} onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// A wall cell as a drop target — accepts a camera dragged from the tree.
function TileDropZone({ idx, children }: { idx: number; children: (isOver: boolean) => ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id: `tile-${idx}`, data: { tileIdx: idx } });
  return <div ref={setNodeRef}>{children(isOver)}</div>;
}
